<script lang="ts">
	import { A, Accordion, AccordionItem, Heading } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { twJoin } from "tailwind-merge";
	import type { Poll } from "#gen/forms/v1/poll_pb.js";
	import type { Page } from "#gen/pages/v2/page_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import PollCardAppearance from "#lib/components/page/components/poll/PollCardAppearance.svelte";
	import PollConfiguration from "#lib/components/page/components/poll/PollConfiguration.svelte";
	import PollHeaderAppearance from "#lib/components/page/components/poll/PollHeaderAppearance.svelte";
	import PollPostVoteAppearance from "#lib/components/page/components/poll/PollPostVoteAppearance.svelte";
	import PollPostVoteConfiguration from "#lib/components/page/components/poll/PollPostVoteConfiguration.svelte";
	import PollPreStartAppearance from "#lib/components/page/components/poll/PollPreStartAppearance.svelte";
	import PollPreStartConfiguration from "#lib/components/page/components/poll/PollPreStartConfiguration.svelte";
	import PollResultCardAppearance from "#lib/components/page/components/poll/PollResultCardAppearance.svelte";
	import PollSchedule from "#lib/components/page/components/poll/PollSchedule.svelte";
	import PollTheme from "#lib/components/page/components/poll/PollTheme.svelte";
	import TimezoneConfiguration from "#lib/components/page/components/poll/TimezoneConfiguration.svelte";
	import { resetEditorState } from "#lib/components/page/components/poll/utils.js";
	import TabItem from "#lib/ui/Tabs/TabItem.svelte";
	import Tabs from "#lib/ui/Tabs/Tabs.svelte";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		page: Writable<Page>;
		poll: Poll;
		readOnly?: boolean;
		onInput?: (poll: Poll) => void;
		onChange?: (poll: Poll) => void;
	}

	const editorState: Writable<Record<string, unknown>> = getContext("editorState");

	const { id, page, poll, readOnly, onChange, onInput }: Props = $props();

	const selectedTab = $derived($editorState[id] || "content");

	const openContent = $state([false, false, false, false, false]);
	const openDesign = $state([false, false, false, false, false, false]);

	function setActiveTab(tabName: string) {
		$editorState[id] = tabName;
		$editorState = resetEditorState($editorState, poll);
	}
</script>

{#snippet readOnlyBanner()}
	<div class="flex w-full items-center gap-1 rounded-lg bg-red-100 p-2 text-sm">
		<Icon iconSet="heroicons" name="information-circle" class="inline-block h-4 w-4" />
		This series is published and cannot be edited.
		<A color={undefined} class="flex gap-1 font-semibold text-gray-900 hover:underline" ref="">View results in analytics <Icon iconSet="heroicons" name="arrow-right" class="inline-block h-4 w-4" /></A>
	</div>
{/snippet}

<Tabs tabStyle="underline" classes={{ content: twJoin("my-0 py-0 px-0 w-full h-full min-h-0 bg-transparent", readOnly && "opacity-50") }} class={twJoin("w-full px-6 pt-6", readOnly && "pointer-events-none opacity-50")}>
	<TabItem open={selectedTab === "content"} onclick={() => setActiveTab("content")}>
		{#snippet titleSlot()}
			<div class="flex items-center gap-1.5">
				<Icon iconSet="heroicons" name="pencil-square" />
				<div class="flex items-center gap-1.5">Content</div>
			</div>
		{/snippet}
		<div class="flex grow flex-col">
			<div class="flex flex-col items-start gap-4 p-6">
				{#if readOnly}
					{@render readOnlyBanner()}
				{/if}
				<div class={twJoin("flex grow flex-col gap-1", readOnly && "opacity-50")}>
					<Heading tag="h4" class="flex items-center justify-between text-lg font-semibold">Content</Heading>
					<P size="sm" secondary>Configure the poll's fields, schedule, and display timing.</P>
				</div>
			</div>
			<Accordion class={twJoin("h-full min-h-0 overflow-y-auto px-6", readOnly && "pointer-events-none opacity-50")} flush>
				<AccordionItem bind:open={openContent[0]} classes={{ button: twJoin("py-4", openContent[0] && "open"), content: "border-0" }}>
					{#snippet header()}Poll fields{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollConfiguration {poll} {onInput} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openContent[1]} classes={{ button: twJoin("py-4", openContent[0] && "open"), content: "border-0" }}>
					{#snippet header()}Timezone{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<TimezoneConfiguration {poll} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openContent[2]} classes={{ button: twJoin("py-4", openContent[1] && "open"), content: "border-0" }}>
					{#snippet header()}Voting schedule{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollSchedule {poll} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openContent[3]} classes={{ button: twJoin("py-4", openContent[2] && "open"), content: "border-0" }}>
					{#snippet header()}Pre-start display{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollPreStartConfiguration {poll} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openContent[4]} classes={{ button: twJoin("py-4", openContent[3] && "open"), content: "border-0" }}>
					{#snippet header()}All results{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollPostVoteConfiguration {poll} {onChange} />
				</AccordionItem>
			</Accordion>
		</div>
	</TabItem>
	<TabItem open={selectedTab === "design"} onclick={() => setActiveTab("design")}>
		{#snippet titleSlot()}
			<div class="flex items-center gap-1.5">
				<Icon iconSet="material-symbols-light" name="palette-outline" />
				<div class="flex items-center gap-1.5">Appearance</div>
			</div>
		{/snippet}
		<div class="flex grow flex-col">
			<div class="flex flex-col items-start gap-4 p-6">
				<div class="flex grow flex-col gap-1">
					<Heading tag="h4" class="flex items-center justify-between text-lg font-semibold">Appearance</Heading>
					<P size="sm" secondary>Customize the theme, colors and content used throughout the poll experience.</P>
				</div>
			</div>
			<Accordion class={twJoin("h-full min-h-0 overflow-y-auto px-6", readOnly && "pointer-events-none")} flush>
				<AccordionItem bind:open={openDesign[0]} classes={{ button: twJoin("py-4", openDesign[0] && "open"), content: "px-4 border-0" }}>
					{#snippet header()}Theme{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollTheme {id} {page} {poll} {onInput} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openDesign[1]} classes={{ button: twJoin("py-4", openDesign[1] && "open"), content: "border-0" }}>
					{#snippet header()}Header{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollHeaderAppearance {id} {page} {poll} {onInput} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openDesign[2]} classes={{ button: twJoin("py-4", openDesign[2] && "open"), content: "border-0" }}>
					{#snippet header()}Default poll appearance{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollCardAppearance {id} {page} {poll} {onInput} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openDesign[3]} classes={{ button: twJoin("py-4", openDesign[3] && "open"), content: "border-0" }}>
					{#snippet header()}Results poll appearance
					{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollResultCardAppearance {id} {page} {poll} {onInput} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openDesign[4]} classes={{ button: twJoin("py-4", openDesign[4] && "open"), content: "border-0" }}>
					{#snippet header()}Pre-start display{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollPreStartAppearance {id} {page} {poll} {onInput} {onChange} />
				</AccordionItem>
				<AccordionItem bind:open={openDesign[5]} classes={{ button: twJoin("py-4", openDesign[5] && "open"), content: "border-0" }}>
					{#snippet header()}All results{/snippet}
					{#snippet arrowup()}<Icon name="chevron-up" class="h-5 w-5" />{/snippet}
					{#snippet arrowdown()}<Icon name="chevron-down" class="h-5 w-5" />{/snippet}
					<PollPostVoteAppearance {id} {page} {poll} {onInput} {onChange} />
				</AccordionItem>
			</Accordion>
		</div>
	</TabItem>
</Tabs>
